import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../Navigation/context/DoctorContext";
import { useToast } from "../../Common/hooks/useToast";
import { BottomNav } from "../../Common/custom_components/bottom_nav/BottomNav";
import { useProfileModule } from "./hooks/profile_module.hook";
import style from "./profile.module.css";

export const DoctorProfile: React.FC = () => {
  const navigate = useNavigate();
  const { doctorProfile, isDoctorProfileLoading } = useDoctorContext();
  const { toast } = useToast();
  const {
    isUploading,
    fileInputRef,
    displayName,
    initials,
    resolvedPhotoUrl,
    getProfilePhotoUrl,
    handleLogout,
    handleAvatarClick,
    handleFileChange,
  } = useProfileModule();

  if (isDoctorProfileLoading) {
    return (
      <div className={style.doctorBody} style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ color: "var(--primary)", fontWeight: 600 }}>Loading profile...</div>
      </div>
    );
  }

  return (
    <div className={style.doctorBody}>
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${style[toast.type]}`}>
          <span className="material-icons-round">
            {toast.type === "success" ? "check_circle" : "error"}
          </span>
          {toast.msg}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Header */}
      <div className={style.docHeader}>
        <div className={style.docHeaderLeft}>
          <button className={style.docHeaderBack} onClick={() => navigate(-1)}>
            <span className="material-icons-round">arrow_back</span>
          </button>
          <div className={style.docHeaderInfo} style={{ marginLeft: "12px" }}>
            <h1>My Profile</h1>
            <span>{displayName}</span>
          </div>
        </div>
        <img
          src="/logo-transparent3.png"
          alt="Pococare"
          style={{ height: "36px" }}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
          }}
        />
      </div>

      {/* Profile Header */}
      <div className={style.profileHeader}>
        <div className={style.avatarWrapper} onClick={handleAvatarClick}>
          <div className={style.profileAvatar}>
            {doctorProfile?.profile_photo && resolvedPhotoUrl && (
              <img
                src={getProfilePhotoUrl(doctorProfile.profile_photo)}
                alt={displayName}
                className={style.profileAvatarImg}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                  const fallback = (e.target as HTMLImageElement)
                    .parentElement?.querySelector(`.${style.avatarFallback}`) as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            )}
            <span
              className={style.avatarFallback}
              style={{ display: doctorProfile?.profile_photo && resolvedPhotoUrl ? "none" : "flex" }}
            >
              {initials}
            </span>
          </div>
          <div className={`${style.avatarOverlay} ${isUploading ? style.uploading : ""}`}>
            {isUploading ? (
              <div className={style.avatarSpinner} />
            ) : (
              <span className="material-icons-round">photo_camera</span>
            )}
          </div>
        </div>
        <div className={style.profileName}>{displayName}</div>
        <div className={style.profileSpecialization}>
          {doctorProfile?.specialization} • {doctorProfile?.qualification}
        </div>
        <div className={style.profileBadges}>
          {doctorProfile?.is_verified && (
            <span className={`${style.profileBadge} ${style.verified}`}>
              <span className="material-icons-round">verified</span>
              Verified
            </span>
          )}
          <span className={`${style.profileBadge} ${style.type}`}>
            <span className="material-icons-round">business</span>
            {doctorProfile?.doctor_type
              ? doctorProfile.doctor_type.charAt(0).toUpperCase() + doctorProfile.doctor_type.slice(1)
              : "Pococare"}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className={style.profileContent} style={{ paddingBottom: "100px" }}>
        {/* Professional Details */}
        <div className={style.infoCard}>
          <h3>Professional Details</h3>
          <div className={style.infoRow}>
            <span className="material-icons-round">badge</span>
            <span className={style.infoRowLabel}>Registration No.</span>
            <span className={style.infoRowValue}>{doctorProfile?.registration_number || "N/A"}</span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">account_balance</span>
            <span className={style.infoRowLabel}>Council</span>
            <span className={style.infoRowValue}>{doctorProfile?.registration_council || "N/A"}</span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">work_history</span>
            <span className={style.infoRowLabel}>Experience</span>
            <span className={style.infoRowValue}>{doctorProfile?.years_of_experience || 0} years</span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">translate</span>
            <span className={style.infoRowLabel}>Languages</span>
            <div className={style.tagRow}>
              {doctorProfile?.languages?.length
                ? doctorProfile.languages.map((lang: string, i: number) => (
                    <span key={i} className={style.tagSmall}>{lang.trim()}</span>
                  ))
                : <span className={style.tagSmall}>N/A</span>}
            </div>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">devices</span>
            <span className={style.infoRowLabel}>Consultation Modes</span>
            <div className={style.tagRow}>
              {doctorProfile?.consultation_modes?.length
                ? doctorProfile.consultation_modes.map((mode: string, i: number) => (
                    <span key={i} className={style.tagSmall}>{mode}</span>
                  ))
                : <span className={style.tagSmall}>N/A</span>}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className={style.infoCard}>
          <h3>Contact</h3>
          <div className={style.infoRow}>
            <span className="material-icons-round">email</span>
            <span className={style.infoRowLabel}>Email</span>
            <span className={style.infoRowValue} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {doctorProfile?.email}
            </span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">phone</span>
            <span className={style.infoRowLabel}>Phone</span>
            <span className={style.infoRowValue}>{doctorProfile?.phone}</span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">location_on</span>
            <span className={style.infoRowLabel}>City</span>
            <span className={style.infoRowValue}>
              {[doctorProfile?.city, doctorProfile?.state].filter(Boolean).join(", ") || "N/A"}
            </span>
          </div>
        </div>

        {/* Menu */}
        <div className={style.menuCard}>
          <Link to="/documents" className={style.menuItem}>
            <span className="material-icons-round">description</span>
            <span className={style.menuLabel}>My Documents</span>
            <span className={`material-icons-round ${style.menuArrow}`}>chevron_right</span>
          </Link>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => e.preventDefault()} className={style.menuItem}>
            <span className="material-icons-round">notifications</span>
            <span className={style.menuLabel}>Notification Settings</span>
            <span className={`material-icons-round ${style.menuArrow}`}>chevron_right</span>
          </a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => e.preventDefault()} className={style.menuItem}>
            <span className="material-icons-round">help</span>
            <span className={style.menuLabel}>Help & Support</span>
            <span className={`material-icons-round ${style.menuArrow}`}>chevron_right</span>
          </a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => e.preventDefault()} className={style.menuItem}>
            <span className="material-icons-round">info</span>
            <span className={style.menuLabel}>About</span>
            <span className={`material-icons-round ${style.menuArrow}`}>chevron_right</span>
          </a>
          <div onClick={handleLogout} className={`${style.menuItem} ${style.danger}`}>
            <span className="material-icons-round">logout</span>
            <span className={style.menuLabel}>Logout</span>
            <span className={`material-icons-round ${style.menuArrow}`}>chevron_right</span>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};
